import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { useToast } from './ToastContainer';

const UserBugReportView = () => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { showSuccess, showError, showInfo } = useToast();
  
  const queryParams = new URLSearchParams(location.search);
  const email = queryParams.get('email');

  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [commentText, setCommentText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`/api/bug-report/public/${id}?email=${encodeURIComponent(email)}`);
      const data = await response.json();
      
      if (response.ok && data.success) {
        setReport(data.bugReport);
      } else {
        throw new Error(data.error || 'Failed to fetch bug report');
      }
    } catch (error) {
      console.error('Error fetching report:', error);
      showError(error.message || 'Unauthorized access or bug report not found.');
      navigate('/');
    } finally {
      setLoading(false);
    }
  }, [id, email, showError, navigate]);

  useEffect(() => {
    if (!email) {
      showError('Unauthorized: No email verification provided in the URL link.');
      navigate('/');
      return;
    }
    fetchReport();
  }, [email, navigate, showError, fetchReport]);

  const handleSubmitReply = async (e) => {
    e.preventDefault();
    if (!commentText.trim()) return;
    
    setSubmitting(true);
    showInfo('Posting reply...', 1000);
    
    try {
      const response = await fetch(`/api/bug-report/public/${id}/comment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, comment: commentText }),
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        showSuccess('Your reply was successfully sent to the administrators!');
        setCommentText('');
        // Refresh the thread to show new comment
        fetchReport();
      } else {
        throw new Error(data.error || 'Failed to post reply');
      }
    } catch (error) {
      console.error('Error posting reply:', error);
      showError(error.message || 'Failed to post your reply. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="loading-container"><h2>Loading Bug Report...</h2></div>;
  }

  if (!report) {
    return null;
  }

  return (
    <div className="admin-container" style={{ maxWidth: '800px', margin: '40px auto', padding: '20px' }}>
      <div className="admin-header">
        <h1>🐛 Bug Report Thread #{report.id}</h1>
        <div style={{ padding: '4px 12px', background: '#3b82f6', color: 'white', borderRadius: '15px', fontWeight: 'bold' }}>
          Status: {report.status.toUpperCase()}
        </div>
      </div>

      <div className="bug-details-card" style={{ background: '#f8fafc', padding: '20px', borderRadius: '8px', border: '1px solid #e2e8f0', marginBottom: '30px' }}>
        <h2 style={{ marginTop: 0, color: '#0f172a' }}>{report.issue_title}</h2>
        <p style={{ color: '#475569', whiteSpace: 'pre-wrap' }}>{report.issue_description}</p>
        <div style={{ marginTop: '15px', fontSize: '0.9em', color: '#64748b' }}>
          Reported on {new Date(report.created_at).toLocaleString()}
        </div>
      </div>

      <div className="bug-history-log" style={{ marginBottom: '40px' }}>
        <h3>Activity Thread</h3>
        {report.history_log && report.history_log.length > 0 ? (
          report.history_log.map((entry, idx) => (
            <div key={idx} style={{ 
              margin: '15px 0', 
              padding: '15px', 
              background: entry.adminName === 'Original Reporter' ? '#eff6ff' : '#fef3c7', 
              borderLeft: `4px solid ${entry.adminName === 'Original Reporter' ? '#3b82f6' : '#f59e0b'}`,
              borderRadius: '6px'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px', fontSize: '0.9em', color: '#64748b' }}>
                <strong style={{ color: '#0f172a' }}>{entry.adminName}</strong>
                <span>{new Date(entry.timestamp).toLocaleString()}</span>
              </div>
              {entry.newStatus !== entry.oldStatus && (
                <div style={{ fontSize: '0.85em', color: '#64748b', marginBottom: entry.comment ? '8px' : '0' }}>
                  <i>Changed status: {entry.oldStatus} ➔ <strong>{entry.newStatus}</strong></i>
                </div>
              )}
              {entry.comment && (
                <div style={{ whiteSpace: 'pre-wrap', color: '#334155' }}>"{entry.comment}"</div>
              )}
            </div>
          ))
        ) : (
          <p style={{ color: '#64748b' }}>No replies or activity yet.</p>
        )}
      </div>

      <div className="bug-reply-section" style={{ background: 'white', padding: '20px', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
        <h3 style={{ marginTop: 0 }}>Add a Reply</h3>
        <form onSubmit={handleSubmitReply}>
          <textarea
            value={commentText}
            onChange={(e) => setCommentText(e.target.value)}
            placeholder="Type your answer, more files, or questions here..."
            style={{ width: '100%', minHeight: '120px', padding: '15px', borderRadius: '6px', border: '1px solid #cbd5e1', marginBottom: '15px', fontFamily: 'inherit' }}
            required
            disabled={submitting}
          />
          <button 
            type="submit" 
            disabled={submitting || !commentText.trim()}
            style={{ 
              background: '#2563eb', color: 'white', border: 'none', padding: '12px 24px', 
              borderRadius: '6px', fontWeight: 'bold', cursor: submitting ? 'not-allowed' : 'pointer' 
            }}
          >
            {submitting ? 'Sending...' : 'Post Reply'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default UserBugReportView;
